import { formatDistanceToNow } from 'date-fns';
import { uk } from 'date-fns/locale';
import { channel } from '../data/channel';
import type { MappedVideo, Playlist, Video } from '../types/video';

export function videoUrl(video: Video): string {
  return video.type === 'short'
    ? `https://www.youtube.com/shorts/${video.id}`
    : `https://www.youtube.com/watch?v=${video.id}`;
}

/** 'hq' always exists; 'max' is sharper but missing on some older uploads */
export function videoThumbnail(video: Video, quality: 'hq' | 'max' = 'hq'): string {
  const file = quality === 'max' ? 'maxresdefault' : 'hqdefault';
  return `https://i.ytimg.com/vi/${video.id}/${file}.jpg`;
}

export function playlistUrl(playlist: Playlist): string {
  return playlist.youtubePlaylistId
    ? `https://www.youtube.com/playlist?list=${playlist.youtubePlaylistId}`
    : `${channel.url}/playlists`;
}

/** Removes #hashtags from Shorts titles */
export function cleanTitle(title: string): string {
  return title.replace(/#[\p{L}\d_]+/gu, '').replace(/\s{2,}/g, ' ').trim();
}

export function formatViews(views: number, withLabel = true): string {
  let value: string;
  if (views >= 1_000_000) value = `${(views / 1_000_000).toFixed(1).replace('.', ',')} млн`;
  else if (views >= 10_000) value = `${Math.round(views / 1000)} тис.`;
  else if (views >= 1000) value = `${(views / 1000).toFixed(1).replace('.', ',')} тис.`;
  else value = `${views}`;
  return withLabel ? `${value} переглядів` : value;
}

export function formatAgo(date: string): string {
  return formatDistanceToNow(new Date(date), { addSuffix: true, locale: uk });
}

export function totalMinutes(list: Video[]): number {
  const seconds = list.reduce(
    (sum, v) => sum + v.duration.split(':').map(Number).reduce((acc, part) => acc * 60 + part, 0),
    0,
  );
  return Math.round(seconds / 60);
}

export function isMapped(video: Video): video is MappedVideo {
  return (
    typeof video.lat === 'number' &&
    typeof video.lng === 'number' &&
    !!video.place &&
    !!video.country &&
    !!video.region
  );
}
